import './AddressData.scss';

import {
  IonCol, IonGrid, IonIcon, IonInput, IonLabel, IonRow,
} from '@ionic/react';
import classNames from 'classnames';
import { add, set } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

import PoiIcon from '@/assets/icons/poi.svg';
import LocationSearchInput from '@/components/PlacesAutocomplete';
import ENVIRONMENT from '@/environments/environment';
import { PostalAddress } from '@/interfaces/CustomerInterface/CustomerInterface';
import { KeyModel } from '@/redux/eDniModal/appConfig/appConfig';
import { getCitiesByPostalCode } from '@/redux/eDniModal/eDniModalAPI';
import { blockCharacters, getRoadType } from '@/utils/formHelper';

interface AddressDataProps {
  defaultValue: { value: string, valueNull: boolean };
  addressData: PostalAddress;
  setAddressData: (arg0: PostalAddress) => void;
  handleLoading: (arg0: boolean) => void;
  setManualMode: (arg0: boolean) => void;
  setErroresForm: (errorAddress: boolean) => void;
}

export default function AddressData({
  defaultValue,
  addressData,
  setAddressData,
  handleLoading,
  setManualMode,
  setErroresForm,
}: AddressDataProps) {
  const [autocompleteZeroResults, setAutocompleteZeroResults] = useState<boolean>(false);
  const [autocompleteAttempts, setAutocompleteAttempts] = useState<number>(0);
  const [addressError, setAddressError] = useState<boolean>(false);
  const [showPostalCode, setShowPostalCode] = useState<boolean>(false);
  const [showAxuxiliary, setShowAuxiliary] = useState<boolean>(false);
  const [showNumber, setShowNumber] = useState<boolean>(false);

  const [postalCodeError, setPostalCodeError] = useState<string>('');
  const locationInputRef = useRef<HTMLDivElement>(null); // Referencia para el contenedor del input
  const [citiesByPostalCodeList, setCitiesByPostalCodeList] = useState<KeyModel[]>([]);

  const {
    placesService,
    placePredictions,
    getPlacePredictions,
  } = usePlacesService({
    apiKey: ENVIRONMENT.MAPS_API_KEY,
    options: {
      input: addressData.address,
      componentRestrictions: {
        country: 'ES',
      },
      types: ['address'],
    },
  });
  const handleAutocompleteSelect = (addressData: PostalAddress, completeAddress?: boolean) => {
    // Verifica si faltan datos esenciales
    // setAddressError(false);
    const isIncompleteAddress = !addressData.postal_code || !addressData.number/*  || !addressData.address */;
    if (isIncompleteAddress) {
      if (autocompleteAttempts === 0) {
        if (!defaultValue.valueNull) {
          setAddressError(true);
          setErroresForm(true);
        } else {
          setAddressError(false);
          setShowNumber(false);
          setShowPostalCode(false);
          setShowAuxiliary(false);
          setErroresForm(false);
        }
        setAddressData({
          ...addressData,
          address: defaultValue.value,
        });
      } else {
        if (addressData.address === '') {
          setShowNumber(false);
          setShowPostalCode(false);
          setShowAuxiliary(false);
          setAddressError(true);
          setErroresForm(true);
          return; // Si la dirección está vacía, no se puede completar
        }
        setShowAuxiliary(true);
        if (!addressData.postal_code) {
          setAutocompleteZeroResults(true);
          setShowPostalCode(true);
        } else {
          setShowPostalCode(false);
        }
        if (!addressData.number) {
          setAutocompleteZeroResults(true);
          setShowNumber(true);
        } else {
          setShowNumber(false);
        }
      }
      setAddressData(addressData);
      //  setAutocompleteZeroResults(true);
      setAutocompleteAttempts((prevAttempts) => prevAttempts + 1);
      return; //  dirección está incompleta
    }

    if (!addressData.postal_code) {
      setShowPostalCode(true);
    } else {
      setShowPostalCode(false);
    }
    if (!addressData.number) {
      setShowNumber(true);
    } else {
      setShowNumber(false);
    }
    setShowAuxiliary(true);
    setAddressData(addressData);
    // Si la dirección es válida, reinicia los estados
    setAutocompleteAttempts(1);
    setAddressError(false);
    setErroresForm(false);
    setPostalCodeError('');

    handleLoading(false);
  };

  const handleFocusAndScroll = (isInputActive: boolean) => {
    setAddressError(true);
    setErroresForm(true);
    setAddressData({
      ...addressData,
      address: '',
      city: '',
      province: '',
      postal_code: '',
      number: '',
      road_type: '',
      aux: '',
    });
    if (isInputActive && locationInputRef.current) {
      const inputElement = locationInputRef.current.querySelector('div');
      if (inputElement) {
        // Retrasa el scroll 1 segundo porque tarda en cargar la lista de direcciones
        setTimeout(() => {
          inputElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 1000);
      }
    }
  };

  const fetchCitiesByPostalCode = async (postalCode = addressData.postal_code) => {
    try {
      if (postalCode !== '') {
        const cities = await getCitiesByPostalCode(postalCode);
        setCitiesByPostalCodeList(cities);
        return cities;
      }
      return [];
    } catch (error) {
      // nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
      handleLoading(false);
    }
  };

  const validatePostalCode = async (value: string, isManual: boolean = false) => {
    const postalCodePattern = /^\d{5}$/;
    if (!value) {
      setPostalCodeError('Indica el código postal');
      setAddressData({
        ...addressData,
        postal_code: '',
      });
      setErroresForm(true);
      return;
    }

    if (!postalCodePattern.test(value)) {
      setPostalCodeError('Código postal inválido');
      setAddressData({
        ...addressData,
        postal_code: value,
      });
      setErroresForm(true);
      return;
    }

    if (value.length === 5) {
      try {
        const cities = await fetchCitiesByPostalCode(value);
        if (cities && cities.length > 0) {
          setAddressData({
            ...addressData,
            city: cities[0].city,
            province: cities[0].province,
            postal_code: value,
          });
          setPostalCodeError('');
          setErroresForm(false);
          if (!addressData.number || addressData.address === '') {
            if (autocompleteAttempts > 1) {
              setAddressError(true);
              setErroresForm(true);
            }
          } else {
            setAddressError(false);
            setErroresForm(false);
          }
        } else if (isManual) {
          setPostalCodeError('Código postal inválido');
          setErroresForm(true);
        } else {
          setPostalCodeError('Código postal inválido');
          setErroresForm(true);
        }
      } catch (error) {
        setPostalCodeError('Código postal inválido');
        setErroresForm(true);
      }
    } else {
      setPostalCodeError('Código postal inválido');
      setAddressData({
        ...addressData,
        postal_code: value,
      });
      setErroresForm(true);
    }
  };

  const addressPredictionFromNumber = (numberData: string) => {
    if (numberData === '' || numberData === undefined) {
      setAddressError(true);
      setShowNumber(true);
      setErroresForm(true);
      return;
    }
    if (!placesService) return;
    setAddressError(true);
    setShowNumber(true);
    setErroresForm(true);
    getPlacePredictions({
      input: `${addressData.address} ${numberData} ${addressData.city} ${addressData.province}`,
      componentRestrictions: { country: 'ES' },
      types: ['address'],
    });
  };
  useEffect(() => {
    if (placePredictions && placePredictions.length > 0 && placesService) {
      setShowPostalCode(true);
      //  setAddressError(true);
      //  setErroresForm(true);
      const prediction = placePredictions[0]; // Tomamos solo el primer resultado
      placesService.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['address_components', 'geometry'],
        },
        (placeDetails) => {
          if (placeDetails) {
            const addressComponents = {
              street_number: placeDetails.address_components?.find(
                (component: any) => component.types.includes('street_number'),
              )?.long_name || '',
              route: placeDetails.address_components?.find(
                (component: any) => component.types.includes('route'),
              )?.long_name || '',
              city: placeDetails.address_components?.find(
                (component: any) => component.types.includes('locality'),
              )?.long_name || '',
              postal_code: placeDetails.address_components?.find(
                (component: any) => component.types.includes('postal_code'),
              )?.long_name || '',
            };
            const roadType = addressComponents.route.split(' ')[0] || '';
            const foundAddress = addressComponents.route;

            const addressDataV: PostalAddress = {
              address: foundAddress,
              city: addressComponents.city,
              number: addressComponents.street_number,
              postal_code: addressComponents.postal_code,
              road_type: getRoadType(roadType),
              province: addressComponents.city,
              aux: addressData?.aux || '',
            };

            if (!addressDataV.postal_code || addressDataV.postal_code === '') {
              setShowPostalCode(true);
              setAddressError(true);
              setAutocompleteAttempts((prevAttempts) => prevAttempts + 1);
              setAutocompleteZeroResults(true);
              setErroresForm(true);
            } else {
              setShowPostalCode(false);
              setAddressError(false);
              setErroresForm(false);
            }

            if (!addressDataV.number || addressDataV.number === '') {
              setAddressError(true);
              setAutocompleteAttempts((prevAttempts) => prevAttempts + 1);
              setAutocompleteZeroResults(true);
              setShowNumber(true);
              setErroresForm(true);
            } else {
              // setShowNumber(false);
              setAddressError(false);
              setErroresForm(false);
            }

            if (addressDataV.number) {
              // console.log('ADDRESS DATA V', addressDataV);
              setAddressData(addressDataV);
            }
          } else {
            console.log('STATUS', status);
          }
        },

      );
    }
  }, [placePredictions]);

  return (
    <div className="addressDataContainer">
      <div className="noManualFormData">
        <IonLabel className="modalTitle">Dirección</IonLabel>
        <IonGrid className="modalFormWrapper">
          <IonRow className="modalFormRow">
            <IonCol>
              <div ref={locationInputRef}>
                <LocationSearchInput
                  showBorder
                  onZeroResults={(value) => {
                    setAutocompleteZeroResults(value);
                  }}
                  defaultValue={defaultValue.value}
                  onSelect={handleAutocompleteSelect}
                  addressError={addressError}
                  onIonInput={handleFocusAndScroll}
                />
              </div>
            </IonCol>
          </IonRow>
          <IonRow className="modalFormRow">
            {showNumber && (
              <IonCol size={showPostalCode && showNumber ? '3' : showPostalCode ? '3' : showNumber ? '3' : ''}>
                <IonInput
                  label="Nº"
                  type="tel"
                  labelPlacement="stacked"
                  className="lower-left-input"
                  value={addressData.number}
                  maxlength={5}
                  onIonInput={(e: any) => {
                    const number = e.target.value;
                    setAddressData({
                      ...addressData,
                      number,
                    });
                    if (!addressData.postal_code || addressData.address === '') {
                      if (autocompleteAttempts > 1) {
                        setAddressError(true);
                        setErroresForm(true);
                      }
                    } else {
                      setAddressError(false);
                      setErroresForm(false);
                    }
                    addressPredictionFromNumber(number);
                  }}
                  onKeyDown={blockCharacters}
                />
              </IonCol>
            )}
            {showPostalCode && (
              <IonCol size={showPostalCode && showNumber ? '3.5' : showPostalCode ? '4' : showNumber ? '4' : ''}>
                <IonInput
                  type="tel"
                  maxlength={5}
                  label="C.P."
                  labelPlacement="stacked"
                  value={addressData.postal_code}
                  className={classNames('inputCommonForm', {
                    'ion-invalid ion-touched': postalCodeError,
                    'ion-valid': !postalCodeError,
                  })}
                  errorText={postalCodeError}
                  onIonInput={(e: any) => validatePostalCode(e.target.value)}
                  onKeyDown={blockCharacters}
                />
                {postalCodeError && <span className="validationError" />}
              </IonCol>
            )}
            {showAxuxiliary && (
              <IonCol size={showPostalCode && showNumber ? '5.5' : showPostalCode ? '7' : showNumber ? '9' : '12'}>
                <IonInput
                  label="Escalera, Portal, Piso..."
                  labelPlacement="stacked"
                  onIonInput={(e: any) => setAddressData({
                    ...addressData,
                    aux: e.target.value,
                  })}
                  value={addressData.aux}
                  className={`lowerRightInput ${showPostalCode || showNumber ? 'lower-right-input' : 'input-end-form'}`}
                />
              </IonCol>
            )}
          </IonRow>
        </IonGrid>
        <div
          className="manualModeWrapper"
          style={{
            cursor: 'pointer',
            marginTop: postalCodeError && showPostalCode ? '20px' : '0px',
          }}
          onClick={() => {
            setManualMode(true);
          }}
        >
          <IonIcon icon={PoiIcon} />
          <p>Introducir dirección manualmente</p>
        </div>
      </div>
    </div>
  );
}
